import { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import gameScoreService from "../api/gameScoreService";

const ROUND_SECONDS = 45;
const MATCH_WAIT = 700;

const STYLES = `
  .crg-root {
    width: 100%;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border-radius: 16px;
    border: 1.5px solid #e4e7ed;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(15,17,23,0.06), 0 1px 4px rgba(15,17,23,0.04);
    user-select: none;
    -webkit-user-select: none;
    font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
  }
  .crg-bar {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px 8px;
    border-bottom: 1.5px solid #f0f2f6;
    background: linear-gradient(180deg, #fafbfd 0%, #ffffff 100%);
  }
  .crg-hint { font-size: 12px; font-weight: 600; color: #0f1117; letter-spacing: -0.01em; }
  .crg-scores { display: flex; align-items: baseline; gap: 8px; flex-shrink: 0; font-variant-numeric: tabular-nums; }
  .crg-score { font-size: 13px; font-weight: 700; color: #0f1117; }
  .crg-time { font-size: 12px; font-weight: 700; color: #1975d1; min-width: 28px; text-align: right; }
  .crg-time.is-urgent { color: #dc2626; }
  .crg-arena {
    position: relative;
    height: 210px;
    overflow: hidden;
    touch-action: manipulation;
    background:
      radial-gradient(circle at 18% 20%, rgba(255,247,214,0.9), transparent 42%),
      repeating-linear-gradient(45deg, rgba(185,28,28,0.04) 0 8px, transparent 8px 16px),
      #f7ecd4;
  }
  .crg-timer {
    position: absolute; left: 0; right: 0; top: 0; height: 8px;
    background: rgba(15,17,23,0.08); z-index: 3; pointer-events: none;
  }
  .crg-timer-fill {
    height: 100%;
    background: linear-gradient(90deg, #22c55e, #eab308 70%, #ef4444);
  }
  .crg-overlay {
    position: absolute; inset: 0; z-index: 5;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 8px; background: rgba(15, 17, 23, 0.82); color: #fff; text-align: center; padding: 16px;
    animation: crg-over-in 420ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .crg-over-kicker { font-size: 11px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: #fbbf24; }
  .crg-over-title { font-size: 34px; font-weight: 800; letter-spacing: -0.04em; line-height: 1; }
  .crg-over-score { font-size: 28px; font-weight: 800; font-variant-numeric: tabular-nums; }
  .crg-over-note { font-size: 12px; color: rgba(255,255,255,0.72); }
  .crg-over-btn {
    margin-top: 6px; border: 0; border-radius: 10px; padding: 8px 14px;
    font-size: 12px; font-weight: 700; background: #b91c1c; color: #fff; cursor: pointer;
  }
  .crg-over-btn:hover { background: #991b1b; }
  .ltm-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(3, minmax(0, 1fr));
    gap: 5px;
    height: 100%;
    padding: 12px 6px 6px;
    box-sizing: border-box;
  }
  .ltm-card {
    display: block;
    position: relative;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    border: 0;
    padding: 0;
    background: none;
    cursor: pointer;
    -webkit-appearance: none;
    appearance: none;
  }
  .ltm-card:disabled { cursor: default; }
  .ltm-inner {
    display: block;
    position: absolute;
    inset: 0;
  }
  .ltm-face {
    display: flex;
    position: absolute;
    inset: 0;
    border-radius: 6px;
    overflow: hidden;
  }
  .ltm-back {
    background: linear-gradient(135deg, #9f1239 0%, #b91c1c 45%, #7f1d1d 100%);
    border: 1.5px solid #7f1d1d;
    align-items: center;
    justify-content: center;
    box-shadow: inset 0 0 0 2px #fde68a;
  }
  .ltm-back-mark {
    display: block;
    width: 18px;
    height: 18px;
    border-radius: 2px;
    border: 1.5px solid #fde68a;
    transform: rotate(45deg);
    background: rgba(253, 230, 138, 0.18);
  }
  .ltm-front {
    background: #fffaf0;
    border: 1.5px solid #b45309;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    padding: 3px 2px 2px;
    box-shadow: inset 0 0 0 1px #fde68a;
    opacity: 0;
    z-index: 1;
  }
  .ltm-card.is-up .ltm-front,
  .ltm-card.is-match .ltm-front { opacity: 1; z-index: 2; }
  .ltm-card.is-up .ltm-back,
  .ltm-card.is-match .ltm-back { opacity: 0; }
  .ltm-card.is-match .ltm-front {
    border-color: #16a34a;
    box-shadow: inset 0 0 0 1px #bbf7d0;
  }
  .ltm-art { flex: 1; width: 100%; display: grid; place-items: center; min-height: 0; }
  .ltm-art svg { width: 70%; height: 70%; }
  .ltm-name {
    font-size: 7.5px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #7f1d1d;
    line-height: 1.1;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    width: 100%;
  }
  @keyframes crg-over-in {
    from { opacity: 0; transform: scale(1.04); }
    to { opacity: 1; transform: scale(1); }
  }
`;

function CucarachaArt() {
  return (
    <svg viewBox="0 0 64 64" aria-hidden>
      <g fill="none" stroke="#3d2914" strokeLinecap="round">
        <path d="M28 18 C22 8 18 6 16 8" strokeWidth="1.6" />
        <path d="M36 18 C42 8 46 6 48 8" strokeWidth="1.6" />
        <path d="M20 30 C8 26 6 20 8 18" strokeWidth="1.7" />
        <path d="M44 30 C56 26 58 20 56 18" strokeWidth="1.7" />
        <path d="M22 46 C10 54 10 60 12 61" strokeWidth="1.7" />
        <path d="M42 46 C54 54 54 60 52 61" strokeWidth="1.7" />
        <ellipse cx="32" cy="44" rx="11" ry="13" fill="#6b4423" stroke="none" />
        <ellipse cx="32" cy="28" rx="12" ry="9" fill="#5c3d1e" stroke="none" />
        <ellipse cx="32" cy="18" rx="6" ry="6" fill="#3d2914" stroke="none" />
      </g>
    </svg>
  );
}

function RataArt() {
  return (
    <svg viewBox="0 0 64 64" aria-hidden>
      <ellipse cx="14" cy="28" rx="6" ry="8" fill="#9ca3af" />
      <ellipse cx="50" cy="28" rx="6" ry="8" fill="#9ca3af" />
      <ellipse cx="32" cy="34" rx="18" ry="14" fill="#6b7280" />
      <ellipse cx="48" cy="30" rx="10" ry="8" fill="#4b5563" />
      <circle cx="52" cy="28" r="1.4" fill="#111" />
      <path d="M8 40 C18 56 40 58 58 40" fill="none" stroke="#9ca3af" strokeWidth="2" />
      <circle cx="58" cy="38" r="2" fill="#f9a8d4" />
    </svg>
  );
}

function MosquitoArt() {
  return (
    <svg viewBox="0 0 64 64" aria-hidden>
      <ellipse cx="24" cy="28" rx="12" ry="7" fill="rgba(148,163,184,0.55)" />
      <ellipse cx="40" cy="28" rx="12" ry="7" fill="rgba(148,163,184,0.55)" />
      <ellipse cx="32" cy="34" rx="5" ry="12" fill="#1f2937" />
      <circle cx="32" cy="20" r="4" fill="#111827" />
      <path d="M32 16 L32 6" stroke="#111827" strokeWidth="1.5" />
      <path d="M28 44 L22 56" stroke="#111827" strokeWidth="1.4" />
      <path d="M36 44 L42 56" stroke="#111827" strokeWidth="1.4" />
    </svg>
  );
}

function HormigaArt() {
  return (
    <svg viewBox="0 0 64 64" aria-hidden>
      <g stroke="#1c1917" strokeLinecap="round" fill="#292524">
        <circle cx="18" cy="32" r="6" />
        <circle cx="32" cy="32" r="5" />
        <ellipse cx="46" cy="32" rx="8" ry="6" />
        <path d="M18 26 L12 16" fill="none" strokeWidth="1.6" />
        <path d="M18 26 L22 16" fill="none" strokeWidth="1.6" />
        <path d="M28 36 L18 50" fill="none" strokeWidth="1.6" />
        <path d="M36 36 L46 50" fill="none" strokeWidth="1.6" />
        <path d="M30 28 L22 20" fill="none" strokeWidth="1.5" />
        <path d="M34 28 L42 20" fill="none" strokeWidth="1.5" />
      </g>
    </svg>
  );
}

function AlacranArt() {
  return (
    <svg viewBox="0 0 64 64" aria-hidden>
      <path d="M12 40 C18 28 28 26 36 32 C44 38 46 48 40 54" fill="none" stroke="#92400e" strokeWidth="5" strokeLinecap="round" />
      <path d="M40 54 L48 44 L52 48" fill="none" stroke="#b45309" strokeWidth="3" strokeLinecap="round" />
      <path d="M20 36 L10 28" stroke="#92400e" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M22 40 L10 44" stroke="#92400e" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="14" cy="38" r="3" fill="#78350f" />
    </svg>
  );
}

function MoscaArt() {
  return (
    <svg viewBox="0 0 64 64" aria-hidden>
      <ellipse cx="22" cy="30" rx="14" ry="10" fill="rgba(191,219,254,0.8)" />
      <ellipse cx="42" cy="30" rx="14" ry="10" fill="rgba(191,219,254,0.8)" />
      <ellipse cx="32" cy="36" rx="8" ry="11" fill="#1e293b" />
      <circle cx="32" cy="24" r="5" fill="#0f172a" />
      <circle cx="30" cy="23" r="1.2" fill="#e2e8f0" />
      <circle cx="34" cy="23" r="1.2" fill="#e2e8f0" />
    </svg>
  );
}

const DECK = [
  { id: "cucaracha", name: "La Cucaracha", art: CucarachaArt },
  { id: "rata", name: "La Rata", art: RataArt },
  { id: "mosquito", name: "El Mosquito", art: MosquitoArt },
  { id: "hormiga", name: "La Hormiga", art: HormigaArt },
  { id: "alacran", name: "El Alacrán", art: AlacranArt },
  { id: "mosca", name: "La Mosca", art: MoscaArt },
];

function shuffle(list) {
  const next = [...list];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function deal() {
  const doubled = DECK.flatMap((item) => [
    { uid: `${item.id}-a`, pair: item.id, name: item.name, Art: item.art },
    { uid: `${item.id}-b`, pair: item.id, name: item.name, Art: item.art },
  ]);
  return shuffle(doubled);
}

export default function PestMemorama({ onGameEnd }) {
  const phaseRef = useRef("idle");
  const timeRef = useRef(ROUND_SECONDS);
  const scoreRef = useRef(0);
  const openRef = useRef([]);
  const lockRef = useRef(false);
  const endingRef = useRef(false);
  const matchedRef = useRef(new Set());
  const comboRef = useRef(0);
  const onGameEndRef = useRef(onGameEnd);
  const cardsRef = useRef(deal());

  const [cards, setCards] = useState(cardsRef.current);
  const [up, setUp] = useState([]);
  const [matched, setMatched] = useState([]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(ROUND_SECONDS);
  const [phase, setPhase] = useState("idle");
  const [isNewBest, setIsNewBest] = useState(false);
  const [rank, setRank] = useState(null);
  const [cleared, setCleared] = useState(false);

  onGameEndRef.current = onGameEnd;

  const finishRound = useCallback(async (won) => {
    if (endingRef.current) return;
    endingRef.current = true;
    phaseRef.current = "over";
    timeRef.current = Math.max(0, timeRef.current);
    if (won) {
      scoreRef.current += Math.round(timeRef.current * 2);
      setScore(scoreRef.current);
    }
    setCleared(Boolean(won));
    setTimeLeft(Math.ceil(timeRef.current));
    setPhase("over");

    try {
      const res = await gameScoreService.submitScore("memorama", scoreRef.current);
      const data = res.data?.data || {};
      setIsNewBest(Boolean(data.isNewBest));
      setRank(data.rank || null);
      onGameEndRef.current?.(data);
    } catch {
      setIsNewBest(false);
      setRank(null);
      onGameEndRef.current?.({ lastScore: scoreRef.current, leaderboard: [] });
    }
  }, []);

  useEffect(() => {
    let last = performance.now();
    let raf = 0;
    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (phaseRef.current === "playing") {
        timeRef.current = Math.max(0, timeRef.current - dt);
        const shown = Math.ceil(timeRef.current);
        setTimeLeft((prev) => (prev !== shown ? shown : prev));
        if (timeRef.current <= 0) finishRound(false);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [finishRound]);

  const flip = (uid) => {
    if (phaseRef.current === "over" || lockRef.current) return;
    if (matchedRef.current.has(uid) || openRef.current.includes(uid)) return;

    if (phaseRef.current === "idle") {
      phaseRef.current = "playing";
      timeRef.current = ROUND_SECONDS;
      setPhase("playing");
      setTimeLeft(ROUND_SECONDS);
    }

    const nextOpen = [...openRef.current, uid];
    openRef.current = nextOpen;
    setUp([...nextOpen]);

    if (nextOpen.length < 2) return;

    lockRef.current = true;
    const [a, b] = nextOpen;
    const cardA = cardsRef.current.find((c) => c.uid === a);
    const cardB = cardsRef.current.find((c) => c.uid === b);
    const isMatch = cardA && cardB && cardA.pair === cardB.pair;

    window.setTimeout(() => {
      if (isMatch) {
        matchedRef.current.add(a);
        matchedRef.current.add(b);
        comboRef.current += 1;
        scoreRef.current += 25 + (comboRef.current - 1) * 5;
        setScore(scoreRef.current);
        setMatched(Array.from(matchedRef.current));
        openRef.current = [];
        setUp([]);
        if (matchedRef.current.size >= cardsRef.current.length) {
          finishRound(true);
        }
      } else {
        comboRef.current = 0;
        openRef.current = [];
        setUp([]);
      }
      lockRef.current = false;
    }, isMatch ? 280 : MATCH_WAIT);
  };

  const restart = () => {
    endingRef.current = false;
    phaseRef.current = "idle";
    timeRef.current = ROUND_SECONDS;
    scoreRef.current = 0;
    openRef.current = [];
    lockRef.current = false;
    matchedRef.current = new Set();
    comboRef.current = 0;
    cardsRef.current = deal();
    setCards(cardsRef.current);
    setUp([]);
    setMatched([]);
    setScore(0);
    setTimeLeft(ROUND_SECONDS);
    setPhase("idle");
    setIsNewBest(false);
    setRank(null);
    setCleared(false);
  };

  const urgent = phase === "playing" && timeLeft <= 8;
  const timerPct = phase === "playing" ? (timeRef.current / ROUND_SECONDS) * 100 : 100;
  const upSet = new Set(up);
  const matchSet = new Set(matched);

  return (
    <Box className="crg-root" component="aside">
      <style>{STYLES}</style>
      <div className="crg-bar">
        <span className="crg-hint">
          {phase === "over" ? "Ronda terminada" : "Encuentra las parejas"}
        </span>
        <div className="crg-scores">
          <span className="crg-score">{score}</span>
          {phase === "playing" && (
            <span className={`crg-time${urgent ? " is-urgent" : ""}`}>{timeLeft}s</span>
          )}
        </div>
      </div>
      <div className="crg-arena">
        {phase !== "idle" && (
          <div className="crg-timer">
            <div className="crg-timer-fill" style={{ width: `${timerPct}%` }} />
          </div>
        )}
        <div className="ltm-grid">
          {cards.map((card) => {
            const shown = upSet.has(card.uid) || matchSet.has(card.uid);
            const Art = card.Art;
            return (
              <button
                key={card.uid}
                type="button"
                className={`ltm-card${shown ? " is-up" : ""}${matchSet.has(card.uid) ? " is-match" : ""}`}
                disabled={phase === "over"}
                onPointerDown={(event) => {
                  if (event.button > 0) return;
                  event.preventDefault();
                  flip(card.uid);
                }}
              >
                <span className="ltm-inner">
                  <span className="ltm-face ltm-back">
                    <span className="ltm-back-mark" />
                  </span>
                  <span className="ltm-face ltm-front">
                    <span className="ltm-art"><Art /></span>
                    <span className="ltm-name">{card.name}</span>
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        {phase === "over" && (
          <div className="crg-overlay">
            <div className="crg-over-kicker">{cleared ? "¡Lotería!" : "Se acabó el tiempo"}</div>
            <div className="crg-over-title">FIN</div>
            <div className="crg-over-score">{score} pts</div>
            <div className="crg-over-note">
              {isNewBest ? "Nuevo récord personal" : rank ? `Quedaste en el puesto ${rank}` : "Mira el ranking abajo"}
            </div>
            <button type="button" className="crg-over-btn" onClick={restart}>
              Jugar de nuevo
            </button>
          </div>
        )}
      </div>
    </Box>
  );
}
